import * as React from 'react';
import { cn } from '../lib/cn';
import { ICON_NAMES } from '../icons/ids';

// Vite glob-imports every SVG in `src/icons/` as a raw string at build time.
const RAW_ICONS = import.meta.glob('../icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Strip the outer <svg> wrapper — we render our own <svg> with consistent
// width/height/viewBox/stroke (Bundle critical-bug: SVGs need explicit
// width+height; without them they expand to fill).
const INNER: Record<string, string> = Object.fromEntries(
  Object.entries(RAW_ICONS).map(([path, raw]) => {
    const id = (path.split('/').pop() ?? '').replace('.svg', '');
    const inner = raw
      .replace(/^[\s\S]*?<svg[^>]*>/, '')
      .replace(/<\/svg>\s*$/, '')
      .trim();
    return [id, inner];
  }),
);

export const ICON_IDS: readonly string[] = ICON_NAMES;

/** Known icon ids: an unknown name is a type error instead of a blank glyph at runtime. */
export type IconId = (typeof ICON_NAMES)[number];

export type IconSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';
const SIZE_PX: Record<IconSize, number> = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 22,
};

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  /** Icon id — matches the SVG filename (kebab-case) under `src/icons/`. */
  name: IconId;
  /** Pixel size — token or arbitrary number. */
  size?: IconSize | number;
  /** Visible name for screen readers. Without it the icon is decorative. */
  title?: string;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 'base', title, className, ...rest }, ref) => {
    const inner = INNER[name];
    const px = typeof size === 'number' ? size : SIZE_PX[size];
    return (
      <svg
        ref={ref}
        viewBox="0 0 18 18"
        width={px}
        height={px}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        role={title ? 'img' : undefined}
        aria-label={title}
        aria-hidden={title ? undefined : true}
        {...rest}
        className={cn('vn-icon', className)}
        dangerouslySetInnerHTML={{ __html: inner ?? '' }}
      />
    );
  },
);
Icon.displayName = 'Icon';
